// ---------------------------------------------------------
// Datei: ProjektvorbereitungBriefing.tsx
// Zweck: Rein lesende Ansicht der Phase "Projektvorbereitung" (Ticket #928) — konsolidiertes
//        Projektbriefing für Monteure aus Kundenstammdaten, Positionen, Technik, Fotos,
//        Subunternehmer und Kommentaren.
// Kontext: Lädt das Briefing über getProjektvorbereitungTemplate (one360-api). Fotos können per
//          Klick rückwirkend markiert/beschriftet werden (PhotoAnnotationEditor) — das Original in
//          Drive bleibt dabei UNVERÄNDERT, die Markierung landet als eigenständige "-markiert"-
//          Datei im selben Ordner (per Nutzer-Rückmeldung: beide Versionen sollen erhalten
//          bleiben). Existiert eine markierte Version, wird sie mit dem Original zu einer Kachel
//          gepaart (pairFotosWithMarkedVersions) und primär angezeigt; "Löschen" im Editor springt
//          zurück auf das unangetastete Original. Springt man per "Löschen" zurück und drückt dann
//          direkt "Übernehmen" ohne neu zu zeichnen, wird die markierte Version gelöscht statt
//          unverändert erneut gespeichert (per Nutzer-Rückmeldung). Fotos werden nach Herkunfts-
//          Unterordner gruppiert angezeigt (Allgemein/VOT/Sonstiges), nicht unmarkiert vermischt.
//          Kunde/Positionen/Fotos sind einklappbar (CollapsibleSection), standardmäßig ausgeklappt.
// Stand: Keine Rohr-/Fitting-Mengen (kein Datenmodell dafür vorhanden).
// ---------------------------------------------------------

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  getProjektvorbereitungTemplate,
  saveRohrFittingNote,
  uploadProjektvorbereitungDokument,
} from '../services/api/projektvorbereitung';
import { dataUrlToFile, getFileAsync, getThumbnailAsync } from '../services/api/drive';
import { PHOTO_JPEG_MIME_TYPE } from '../services/imageService';
import { useToast } from '../contexts/ToastContext';
import { sanitizeAddress } from '../utils/address';
import MapsButtons from './MapsButtons';
import PhotoAnnotationEditor from './PhotoAnnotationEditor';
import DocumentPreviewModal from './DocumentPreviewModal';
import CaptionIndicator from './CaptionIndicator';
import FileUploadModal, { type UploadFile } from './FileUploadModal';
import {
  usePhotoAnnotationGallery,
  withMarkedFileNameSuffix,
  isMarkedFileName,
  type PhotoPair,
} from '../hooks/usePhotoAnnotationGallery';
import type { ProjektvorbereitungFoto, ProjektvorbereitungTemplate } from '../interfaces/projektvorbereitung';
import { useTemplateCacheStore } from '../store/templateCacheStore';

// PDFs (z. B. Auftragsbestätigungen im Bilder-Ordner) lassen sich nicht mit dem Zeichen-Canvas
// markieren — Klick öffnet stattdessen eine reine Lesevorschau (DocumentPreviewModal), keine
// Bearbeitung.
const isPdfFoto = (foto: ProjektvorbereitungFoto): boolean => foto.mimeType === 'application/pdf';

// Ticket #1055: die Zuordnung eines Rohranschluss-Fotos zu seinem echten Drive-Ordner passiert
// jetzt serverseitig (GetProjektvorbereitungTemplateCommand.BuildRohranschlussFotosAsync, per
// GetDriveFileMetadataAsync/FindDriveFileByNameInFolderAsync) — das Frontend bekommt bereits
// fertig gepaarte Original/Markiert-Objekte inkl. der echten FolderId zurück, statt selbst per
// HubSpot-Link-ID zu raten oder isoliert nachzuladen.
type RohranschlussFotoWithFolder = ProjektvorbereitungFoto & { folderId?: string | null };

interface ProjektvorbereitungBriefingProps {
  projectId: string;
  phaseId: string;
  disabled?: boolean;
}

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
}

// Klappt Kunde/Positionen/Fotos ein- und ausblenden (Nutzeranfrage), standardmäßig ausgeklappt,
// damit sich am bisherigen Verhalten nichts ändert, bis aktiv eingeklappt wird.
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <button
        type="button"
        onClick={() => setIsOpen((previous) => !previous)}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={isOpen}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        <svg
          className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform shrink-0 ml-2 ${
            isOpen ? 'rotate-180' : ''
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {isOpen && <div className="mt-3">{children}</div>}
    </section>
  );
};

// Begrenzt, wie viele Thumbnail-Anfragen gleichzeitig laufen — bei vielen Fotos (z. B. großer
// VOT-/Sonstiges-Ordner) sollen nicht alle Anfragen auf einmal an Verbindung/Backend gehen,
// gerade auf mobilen Verbindungen vor Ort. Statt eines Big-Bang-`Promise.all` über alle fehlenden
// Thumbnails laufen nur so viele parallel wie hier konfiguriert; die Kacheln füllen sich
// nacheinander, sobald ihr Thumbnail fertig ist.
const THUMBNAIL_LOAD_CONCURRENCY = 3;

type PairedFoto = PhotoPair<ProjektvorbereitungFoto>;

// Fallback für den Fall, dass ein Foto (z. B. aus einer älteren Backend-Antwort ohne das
// Ordner-Feld) keinen Herkunfts-Ordner mitliefert — ohne diesen Fallback wäre der Gruppen-Key
// `undefined` (fehlender React-"key"-Prop-Warnhinweis auf der Fotos-Gruppen-<div>).
const DEFAULT_FOTO_ORDNER = 'Allgemein';

// Dateinamen sind nur INNERHALB eines Ordners eindeutig — dieselbe Kamera kann in `Bilder` UND
// `Bilder/VOT`/`Sonstiges` eine gleich benannte Datei ablegen. Der Pairing-Key muss deshalb immer
// Ordner + Name kombinieren, sonst kann eine "-markiert"-Datei aus dem falschen Ordner dem
// Original zugeordnet werden (Autoreview-Finding).
const fotoPairKey = (foto: ProjektvorbereitungFoto): string =>
  `${foto.ordner || DEFAULT_FOTO_ORDNER}::${foto.name}`;

// Original und markierte Version liegen als zwei getrennte Dateien im selben Ordner (das
// Original wird beim Markieren nie überschrieben, siehe handleSaveAnnotation) — hier werden sie
// per Dateinamens-Konvention (`<original>-markiert.<ext>`) zu je einer Kachel zusammengeführt,
// damit man nicht beide Versionen unmarkiert nebeneinander in der Galerie sieht.
const pairFotosWithMarkedVersions = (fotos: ProjektvorbereitungFoto[]): PairedFoto[] => {
  const byKey = new Map(fotos.map((foto) => [fotoPairKey(foto), foto] as const));
  const consumedMarkedKeys = new Set<string>();
  const pairs: PairedFoto[] = [];

  for (const foto of fotos) {
    if (isMarkedFileName(foto.name)) {
      continue;
    }
    const markedKey = `${foto.ordner || DEFAULT_FOTO_ORDNER}::${withMarkedFileNameSuffix(foto.name)}`;
    const marked = byKey.get(markedKey);
    if (marked) {
      consumedMarkedKeys.add(markedKey);
    }
    pairs.push({ original: foto, marked });
  }

  // Verwaiste "-markiert"-Dateien (z. B. Original zwischenzeitlich außerhalb der App gelöscht)
  // trotzdem als eigene Kachel zeigen statt sie kommentarlos zu verschlucken.
  for (const foto of fotos) {
    if (isMarkedFileName(foto.name) && !consumedMarkedKeys.has(fotoPairKey(foto))) {
      pairs.push({ original: foto });
    }
  }

  return pairs;
};

// Gruppiert nach Herkunfts-Ordner, Reihenfolge nach erstem Auftreten (Backend liefert bereits in
// fester Reihenfolge: Allgemein, VOT, Sonstiges) — damit Fotos aus verschiedenen Quellen nicht
// unmarkiert vermischt angezeigt werden.
const groupPairsByOrdner = (
  pairs: PairedFoto[]
): Array<{ ordner: string; pairs: PairedFoto[] }> => {
  const groups: Array<{ ordner: string; pairs: PairedFoto[] }> = [];
  for (const pair of pairs) {
    const ordner = pair.original.ordner || DEFAULT_FOTO_ORDNER;
    const group = groups.find((g) => g.ordner === ordner);
    if (group) {
      group.pairs.push(pair);
    } else {
      groups.push({ ordner, pairs: [pair] });
    }
  }
  return groups;
};

// Spiegelt GetProjektvorbereitungTemplateCommand.FotoOrdner (one360-api) — dieselbe Unterordner-
// Struktur wird gebraucht, um eine neu markierte Foto-Version in denselben Ordner wie das
// Original hochzuladen.
const ordnerToDirectories = (ordner: string): string[] => {
  switch (ordner) {
    case 'VOT':
      return ['Bilder', 'VOT'];
    case 'Sonstiges':
      return ['Bilder', 'Sonstiges'];
    default:
      return ['Bilder'];
  }
};

const toBriefingFile = (uploadFile: UploadFile): File =>
  uploadFile.file ?? dataUrlToFile(uploadFile.dataUrl, uploadFile.name);

const ProjektvorbereitungBriefing: React.FC<ProjektvorbereitungBriefingProps> = ({ projectId, disabled = false }) => {
  const { showToast } = useToast();
  // Ein verspäteter Upload darf nach einem Routenwechsel weder das neue Projekt überschreiben
  // noch seinen Cache verändern. Die Ref ist innerhalb asynchroner Handler immer aktuell.
  const activeProjectIdRef = useRef(projectId);
  const [template, setTemplate] = useState<ProjektvorbereitungTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [previewFoto, setPreviewFoto] = useState<ProjektvorbereitungFoto | null>(null);
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Googles thumbnailLink lässt sich nicht direkt in <img src> laden (siehe getThumbnailAsync-
  // Kommentar) — Thumbnails werden stattdessen übers Backend geholt und hier als Data-URL
  // zwischengespeichert, analog zum thumbnailCache-Muster in DocumentChecklist.tsx.
  const [thumbnailCache, setThumbnailCache] = useState<Record<string, string>>({});
  // Fotos, deren Thumbnail-Ladeversuch fehlgeschlagen ist (Netzwerk-/Backendfehler) — ohne das
  // würde die Kachel dauerhaft den "wird geladen"-Skeleton zeigen, obwohl kein Request mehr läuft
  // (Autoreview-Finding), da `getThumbnailAsync` Fehler als `null` statt als Exception meldet.
  const [failedThumbnailIds, setFailedThumbnailIds] = useState<Record<string, boolean>>({});

  // Ticket #1055: Rohre-und-Fittinge-Freitextnotiz, nur für Ops-Manager editierbar. Lokaler
  // Entwurfsstate getrennt vom geladenen template.rohreFittinge, damit Tippen nicht bei jedem
  // Tastenanschlag den ganzen Template-State neu rendert und ein fehlgeschlagenes Speichern den
  // Entwurf nicht verwirft.
  const [rohreFittingeDraft, setRohreFittingeDraft] = useState('');
  const [savingRohreFittinge, setSavingRohreFittinge] = useState(false);
  // Ticket #1139: separat vom Foto-/Markierungs-Upload, da die Dateien in einem eigenen,
  // allen Monteuren sichtbaren Briefing-Ordner liegen und nur Ops-Manager ihn beschreiben dürfen.
  const [showBriefingUploadModal, setShowBriefingUploadModal] = useState(false);
  const [pendingBriefingFiles, setPendingBriefingFiles] = useState<UploadFile[]>([]);
  const [uploadingBriefingFiles, setUploadingBriefingFiles] = useState(false);

  useLayoutEffect(() => {
    activeProjectIdRef.current = projectId;
    // PhaseScreen kann diese Komponente beim Projektwechsel wiederverwenden. Auswahl, Modal und
    // Ladezustand gehören immer ausschließlich zum aktuell geöffneten Projekt.
    setShowBriefingUploadModal(false);
    setPendingBriefingFiles([]);
    setUploadingBriefingFiles(false);
  }, [projectId]);

  useEffect(() => {
    setRohreFittingeDraft(template?.rohreFittinge ?? '');
  }, [template?.rohreFittinge]);

  const handleSaveRohreFittinge = async () => {
    setSavingRohreFittinge(true);
    try {
      await saveRohrFittingNote(projectId, rohreFittingeDraft);
      setTemplate((previous) => (previous ? { ...previous, rohreFittinge: rohreFittingeDraft } : previous));
      showToast('success', 'Notiz gespeichert', 3000);
    } catch (saveError) {
      showToast(
        'error',
        saveError instanceof Error ? saveError.message : 'Notiz konnte nicht gespeichert werden',
        4000
      );
    } finally {
      setSavingRohreFittinge(false);
    }
  };

  const handleBriefingFilesSelected = (selectedFiles: UploadFile[]) => {
    if (disabled) return;

    const knownNames = new Set([
      ...(template?.briefingDokumente ?? []).map((file) => file.name),
      ...pendingBriefingFiles.map((file) => file.name),
    ].map((name) => name.trim().toLocaleLowerCase('de-DE')));
    const newFiles = selectedFiles.filter((file) => {
      const normalizedName = file.name.trim().toLocaleLowerCase('de-DE');
      if (knownNames.has(normalizedName)) return false;
      knownNames.add(normalizedName);
      return true;
    });
    if (newFiles.length !== selectedFiles.length) {
      showToast('error', 'Bereits vorhandene Dateien wurden nicht erneut hinzugefügt.', 4000);
    }
    setPendingBriefingFiles((previous) => [...previous, ...newFiles]);
    setShowBriefingUploadModal(false);
  };

  const handleBriefingUpload = async () => {
    if (disabled || !template || pendingBriefingFiles.length === 0 || uploadingBriefingFiles) return;

    const selectedFiles = [...pendingBriefingFiles];
    let updatedTemplate = template;
    const uploadProjectId = projectId;
    setUploadingBriefingFiles(true);
    try {
      // Sequenziell, damit ein Doppelklick nicht parallel denselben Namen ablegt. Die vom Upload
      // zurückgelieferte Datei wird sofort lokal ergänzt: schlägt eine spätere Datei fehl, bleiben
      // bereits erfolgreiche Uploads sichtbar und können nicht versehentlich erneut hochgeladen
      // werden. Ein sofortiger Drive-Reload wäre wegen dessen Eventual Consistency weniger stabil.
      for (const selectedFile of selectedFiles) {
        const uploadedDocument = await uploadProjektvorbereitungDokument(
          projectId,
          updatedTemplate.kunde.name,
          toBriefingFile(selectedFile)
        );
        // Der Request kann während eines Projektwechsels zurückkehren. Dann wurde die Datei
        // korrekt im ursprünglichen Projekt gespeichert, darf aber nicht in das neue Briefing
        // oder dessen Cache übernommen werden.
        if (activeProjectIdRef.current !== uploadProjectId) return;
        updatedTemplate = {
          ...updatedTemplate,
          briefingDokumente: [
            ...(updatedTemplate.briefingDokumente ?? []).filter((document) => document.id !== uploadedDocument.fileId),
            {
              id: uploadedDocument.fileId,
              name: uploadedDocument.fileName,
              mimeType: selectedFile.type,
              webViewLink: uploadedDocument.webViewLink,
            },
          ],
        };
        setTemplate(updatedTemplate);
        useTemplateCacheStore.getState().setProjektvorbereitungTemplate(uploadProjectId, updatedTemplate);
        setPendingBriefingFiles((previous) => previous.filter((file) => file.id !== selectedFile.id));
      }
      showToast('success', `${selectedFiles.length} Briefing-Dokument(e) hochgeladen`, 3000);
    } catch (uploadError) {
      if (activeProjectIdRef.current !== uploadProjectId) return;
      showToast(
        'error',
        uploadError instanceof Error ? uploadError.message : 'Briefing-Dokument konnte nicht hochgeladen werden',
        4000
      );
    } finally {
      if (activeProjectIdRef.current === uploadProjectId) {
        setUploadingBriefingFiles(false);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      // Ticket #1055: Nutzer-Rückmeldung "es funktioniert offline nicht" — dieser Endpunkt hatte
      // bisher keine Persistenz (anders als dealsStore/documentState). Offline: sofort aus dem
      // zuletzt erfolgreich geladenen Cache anzeigen statt einen aussichtslosen Live-Request zu
      // versuchen. Online: weiterhin "network-first" (Daten können sich serverseitig ändern),
      // Cache dient nur als Fallback, falls der Live-Request trotz vermeintlicher Verbindung
      // fehlschlägt (z. B. instabiles mobiles Netz).
      const cached = useTemplateCacheStore.getState().projektvorbereitung[projectId];
      try {
        setLoading(true);
        setError(null);
        if (typeof navigator !== 'undefined' && !navigator.onLine && cached) {
          if (isMounted) {
            setTemplate(cached);
          }
          return;
        }
        const response = await getProjektvorbereitungTemplate(projectId);
        if (isMounted) {
          setTemplate(response);
        }
        useTemplateCacheStore.getState().setProjektvorbereitungTemplate(projectId, response);
      } catch (loadError) {
        if (isMounted) {
          if (cached) {
            setTemplate(cached);
          } else {
            setError(
              loadError instanceof Error
                ? loadError.message
                : 'Projektbriefing konnte nicht geladen werden'
            );
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [projectId]);

  useEffect(() => {
    if (!template || template.fotos.length === 0) {
      return;
    }

    const missing = template.fotos.filter((foto) => !thumbnailCache[foto.id] && foto.thumbnailLink);
    if (missing.length === 0) {
      return;
    }

    let isMounted = true;
    let nextIndex = 0;

    // Jeder "Worker" holt sich per gemeinsamem Index das nächste noch offene Foto — so laufen
    // nie mehr als THUMBNAIL_LOAD_CONCURRENCY Anfragen gleichzeitig, und jedes Thumbnail landet
    // sofort im Cache, sobald es fertig ist, statt erst wenn alle fertig sind.
    const runWorker = async (): Promise<void> => {
      while (isMounted) {
        const currentIndex = nextIndex++;
        if (currentIndex >= missing.length) {
          return;
        }
        const foto = missing[currentIndex];
        const content = await getThumbnailAsync(foto.id, foto.thumbnailLink);
        if (!isMounted) {
          continue;
        }
        if (!content) {
          setFailedThumbnailIds((previous) => ({ ...previous, [foto.id]: true }));
          continue;
        }
        const dataUrl = `data:${content.mimeType};base64,${content.base64Content}`;
        setThumbnailCache((previous) => ({ ...previous, [foto.id]: dataUrl }));
      }
    };

    const workerCount = Math.min(THUMBNAIL_LOAD_CONCURRENCY, missing.length);
    Promise.all(Array.from({ length: workerCount }, runWorker));

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  // Rohranschluss-Fotos sind NICHT Teil von template.fotos (eigenständig serverseitig aufgelöst,
  // s. GetProjektvorbereitungTemplateCommand.BuildRohranschlussFotosAsync) — der obige Effekt
  // erfasst sie deshalb nie. Fehlt einem Foto ein thumbnailLink (Fallback-Fall ohne ermittelten
  // Ordner), wird stattdessen der volle Dateiinhalt direkt per fileId geladen, analog zur Editor-/
  // Vorschau-Ladefunktion.
  useEffect(() => {
    const rohranschlussFotos = template?.technik.inneneinheitRohranschlussFotos ?? [];
    if (rohranschlussFotos.length === 0) {
      return;
    }
    const displayFotos = rohranschlussFotos.map((entry) => entry.marked ?? entry.original);
    const missing = displayFotos.filter((foto) => !thumbnailCache[foto.id]);
    if (missing.length === 0) {
      return;
    }

    let isMounted = true;
    (async () => {
      for (const foto of missing) {
        const content = foto.thumbnailLink
          ? await getThumbnailAsync(foto.id, foto.thumbnailLink)
          : await getFileAsync(foto.id);
        if (!isMounted) {
          return;
        }
        if (content) {
          setThumbnailCache((previous) => ({
            ...previous,
            [foto.id]: `data:${content.mimeType};base64,${content.base64Content}`,
          }));
        } else {
          setFailedThumbnailIds((previous) => ({ ...previous, [foto.id]: true }));
        }
      }
    })();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.technik.inneneinheitRohranschlussFotos]);

  const {
    editingPair,
    editorImageDataUrl,
    editorOriginalImageDataUrl,
    editorLoading,
    editorError,
    savingAnnotation,
    openAnnotation: handleOpenAnnotation,
    cancelAnnotation: handleCancelAnnotation,
    saveAnnotation: handleSaveAnnotation,
    deleteMarkedAnnotation: handleDeleteMarkedAnnotation,
  } = usePhotoAnnotationGallery<ProjektvorbereitungFoto>({
    projectId,
    getUploadTarget: (original) => ordnerToDirectories(original.ordner),
    onSaved: (result, caption, pair, annotatedDataUrl) => {
      const { original } = pair;
      const markedFileName = withMarkedFileNameSuffix(original.name);
      setTemplate((previous) => {
        if (!previous) {
          return previous;
        }
        // Per fileId ODER Dateinamen matchen (nicht nur pair.marked?.id) — deckt auch den
        // Randfall ab, dass die markierte Datei bereits ohne zugehöriges Original in der Liste
        // stand (z. B. Original zwischenzeitlich gelöscht) und hier erneut bearbeitet wird; sonst
        // würde dieselbe Datei doppelt in der Liste landen statt aktualisiert zu werden.
        const existingIndex = previous.fotos.findIndex(
          (foto) => foto.id === result.fileId || foto.name === markedFileName
        );
        const updatedFoto: ProjektvorbereitungFoto =
          existingIndex >= 0
            ? {
                ...previous.fotos[existingIndex],
                // Immer auf result.fileId aktualisieren, nicht die alte id beibehalten — findet
                // replaceIfExists die vorhandene Datei nicht (Namensabweichung o. Ä.) und legt
                // stattdessen eine neue an, würde der lokale State sonst dauerhaft auf eine
                // veraltete, nicht mehr existierende fileId zeigen (bis zum nächsten Neuladen).
                id: result.fileId,
                name: result.fileName ?? markedFileName,
                thumbnailLink: result.thumbnailLink ?? previous.fotos[existingIndex].thumbnailLink,
                webViewLink: result.webViewLink ?? previous.fotos[existingIndex].webViewLink,
                description: result.description ?? caption,
              }
            : {
                id: result.fileId,
                name: result.fileName ?? markedFileName,
                mimeType: PHOTO_JPEG_MIME_TYPE,
                webViewLink: result.webViewLink,
                thumbnailLink: result.thumbnailLink,
                description: result.description ?? caption,
                ordner: original.ordner,
              };
        const fotos =
          existingIndex >= 0
            ? previous.fotos.map((foto, index) => (index === existingIndex ? updatedFoto : foto))
            : [...previous.fotos, updatedFoto];
        return { ...previous, fotos };
      });
      // Die gerade gespeicherte, annotierte Version liegt schon lokal vor — direkt als
      // Thumbnail übernehmen statt sie erneut übers Backend zu laden.
      setThumbnailCache((previous) => ({ ...previous, [result.fileId]: annotatedDataUrl }));
    },
    onDeleted: (markedId) => {
      setTemplate((previous) =>
        previous
          ? { ...previous, fotos: previous.fotos.filter((foto) => foto.id !== markedId) }
          : previous
      );
      setThumbnailCache((previous) => {
        const next = { ...previous };
        delete next[markedId];
        return next;
      });
    },
    onError: (message) => showToast('error', message, 4000),
    onSuccess: (message) => showToast('success', message, 3000),
  });

  // Rohranschluss-Fotos sind eine eigenständige Liste (template.technik.inneneinheitRohranschlussFotos,
  // s. oben) statt Teil von template.fotos — brauchen deshalb eine zweite, unabhängige Instanz des
  // Markierungs-State-Automaten. Der Zielordner ist die vom Backend ermittelte echte FolderId statt
  // eines Directories-Pfads (s. uploadMarkedPhotoAsync/getUploadTarget).
  const {
    editingPair: editingRohranschlussPair,
    editorImageDataUrl: rohranschlussEditorImageDataUrl,
    editorOriginalImageDataUrl: rohranschlussEditorOriginalImageDataUrl,
    editorLoading: rohranschlussEditorLoading,
    editorError: rohranschlussEditorError,
    savingAnnotation: savingRohranschlussAnnotation,
    openAnnotation: handleOpenRohranschlussAnnotation,
    cancelAnnotation: handleCancelRohranschlussAnnotation,
    saveAnnotation: handleSaveRohranschlussAnnotation,
    deleteMarkedAnnotation: handleDeleteMarkedRohranschlussAnnotation,
  } = usePhotoAnnotationGallery<RohranschlussFotoWithFolder>({
    projectId,
    getUploadTarget: (original) => (original.folderId ? { folderId: original.folderId } : []),
    onSaved: (result, caption, pair, annotatedDataUrl) => {
      const { original } = pair;
      setTemplate((previous) => {
        if (!previous) {
          return previous;
        }
        const updatedMarked: ProjektvorbereitungFoto = {
          id: result.fileId,
          name: result.fileName ?? withMarkedFileNameSuffix(original.name),
          mimeType: PHOTO_JPEG_MIME_TYPE,
          webViewLink: result.webViewLink,
          thumbnailLink: result.thumbnailLink,
          description: result.description ?? caption,
          ordner: 'Technik'
        };
        return {
          ...previous,
          technik: {
            ...previous.technik,
            inneneinheitRohranschlussFotos: previous.technik.inneneinheitRohranschlussFotos.map((entry) =>
              entry.original.id === original.id ? { ...entry, marked: updatedMarked } : entry
            )
          }
        };
      });
      setThumbnailCache((previous) => ({ ...previous, [result.fileId]: annotatedDataUrl }));
    },
    onDeleted: (markedId, pair) => {
      setTemplate((previous) =>
        previous
          ? {
              ...previous,
              technik: {
                ...previous.technik,
                inneneinheitRohranschlussFotos: previous.technik.inneneinheitRohranschlussFotos.map((entry) =>
                  entry.original.id === pair.original.id ? { ...entry, marked: undefined } : entry
                )
              }
            }
          : previous
      );
      setThumbnailCache((previous) => {
        const next = { ...previous };
        delete next[markedId];
        return next;
      });
    },
    onError: (message) => showToast('error', message, 4000),
    onSuccess: (message) => showToast('success', message, 3000),
  });

  const handleOpenPreview = async (foto: ProjektvorbereitungFoto) => {
    setPreviewFoto(foto);
    setPreviewDataUrl(null);
    setPreviewLoading(true);
    try {
      const content = await getFileAsync(foto.id);
      if (!content) {
        throw new Error('Vorschau konnte nicht geladen werden');
      }
      setPreviewDataUrl(`data:${content.mimeType};base64,${content.base64Content}`);
    } catch (loadError) {
      showToast(
        'error',
        loadError instanceof Error ? loadError.message : 'Vorschau konnte nicht geladen werden',
        4000
      );
      setPreviewFoto(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewFoto(null);
    setPreviewDataUrl(null);
  };

  // Fotos sind Freihand-markierbar, PDFs (z. B. Auftragsbestätigungen) nicht — je nach Dateityp
  // öffnet ein Klick entweder den Markierungs-Editor oder eine reine Vorschau. Nur Ops-Manager
  // (identischer Backend-Check wie VOTs canResetVotToDraft, Ticket #1055 per Nutzer-Rückmeldung)
  // dürfen überhaupt markieren — alle anderen bekommen für Fotos ebenfalls nur die reine
  // Lesevorschau, nie den Markierungs-Editor.
  const handleOpenFoto = (pair: PairedFoto) => {
    const displayFoto = pair.marked ?? pair.original;
    if (isPdfFoto(displayFoto) || !template?.isOpsManager) {
      handleOpenPreview(displayFoto);
    } else {
      handleOpenAnnotation(pair);
    }
  };

  // Analog zu handleOpenFoto, aber für die eigenständige Rohranschluss-Liste: ohne ermittelte
  // FolderId (Fallback-Fall, s. GetProjektvorbereitungTemplateCommand) bleibt es bei reiner
  // Vorschau, da der echte Zielordner für eine Markierung sonst unbekannt wäre.
  const handleOpenRohranschlussFoto = (pair: PhotoPair<RohranschlussFotoWithFolder>) => {
    const displayFoto = pair.marked ?? pair.original;
    if (isPdfFoto(displayFoto) || !pair.original.folderId || !template?.isOpsManager) {
      handleOpenPreview(displayFoto);
    } else {
      handleOpenRohranschlussAnnotation(pair);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
          <p className="mt-5 text-base font-semibold text-gray-900 dark:text-white">
            Projektbriefing wird geladen
          </p>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        {error || 'Projektbriefing konnte nicht geladen werden'}
      </div>
    );
  }

  const kundeName = [template.kunde.contactFirstName, template.kunde.contactLastName]
    .filter(Boolean)
    .join(' ');
  // objekt___anschrift kommt aus HubSpot oft mit eingebettetem HTML (aus Google Sheets
  // eingefügte Rich-Text-Links zu einer Maps-Suche) — bereinigen statt roh anzuzeigen, und
  // einen eigenen, sicher konstruierten Maps-Link bauen statt das HubSpot-HTML zu übernehmen.
  const sanitizedAdresse = template.kunde.adresse ? sanitizeAddress(template.kunde.adresse) : '';
  const hasTechnik = Boolean(
    template.technik.waermepumpenTyp ||
      template.technik.kernbohrer ||
      template.technik.standortAusseneinheitGeplant ||
      template.technik.standortInneneinheitGeplant ||
      template.technik.inneneinheitRohranschluss ||
      template.technik.inneneinheitRohranschlussFotos.length > 0
  );
  const hasKommentare = Boolean(template.kommentare.dealNotizen || template.kommentare.bemerkungen);
  // Bei einem gestaffelten Rollout kann kurzzeitig noch eine ältere API-Antwort ohne das neue
  // Feld eintreffen. Bis beide Deployments aktualisiert sind, bleibt die Briefing-Ansicht daher
  // stabil und behandelt das fehlende Feld wie eine leere Dokumentliste.
  const briefingDokumente = template.briefingDokumente ?? [];
  // Rohranschluss ist oft (ein oder mehrere) Foto-Link(s) statt Freitext — erscheint als eigene
  // Kacheln direkt im Technik-Bereich (per Nutzer-Rückmeldung). Bereits serverseitig auf den
  // echten Drive-Ordner zurückgeführt (s. Interface-Kommentar oben) — komplett unabhängig von
  // template.fotos, daher hier keine Dopplungs-Filterung mehr nötig.
  const rohranschlussPairs: PhotoPair<RohranschlussFotoWithFolder>[] = template.technik.inneneinheitRohranschlussFotos.map(
    (entry) => ({
      original: { ...entry.original, folderId: entry.folderId },
      marked: entry.marked
    })
  );

  return (
    <div className="space-y-4">
      <CollapsibleSection title="Kunde">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Projekt</dt>
            <dd className="text-gray-900 dark:text-white">{template.kunde.name || '—'}</dd>
          </div>
          {kundeName && (
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Ansprechpartner</dt>
              <dd className="text-gray-900 dark:text-white">{kundeName}</dd>
            </div>
          )}
          {template.kunde.contactPhone && (
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Telefon</dt>
              <dd className="text-gray-900 dark:text-white">{template.kunde.contactPhone}</dd>
            </div>
          )}
          {template.kunde.contactEmail && (
            <div>
              <dt className="text-gray-500 dark:text-gray-400">E-Mail</dt>
              <dd className="text-gray-900 dark:text-white">{template.kunde.contactEmail}</dd>
            </div>
          )}
        </dl>

        {sanitizedAdresse && (
          // template.kunde.adresse stammt aus der HubSpot-Objekt-Adresse (backend:
          // HubSpotFieldCatalog.ObjectAddress), NICHT aus einer beim Kontakt gespeicherten
          // Kunden-Adresse — eigener, zentrierter "Objekt Adresse"-Block innerhalb von "Kunde"
          // (Nutzer-Rückmeldung).
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Objekt Adresse</p>
            <p className="text-sm text-gray-900 dark:text-white">{sanitizedAdresse}</p>
            <MapsButtons address={sanitizedAdresse} className="mt-2 flex items-center justify-center gap-2" />
          </div>
        )}
      </CollapsibleSection>

      {hasKommentare && (
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Kommentare &amp; Besonderheiten
          </h2>
          {template.kommentare.dealNotizen && (
            <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap mb-2">
              {template.kommentare.dealNotizen}
            </p>
          )}
          {template.kommentare.bemerkungen && (
            <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
              {template.kommentare.bemerkungen}
            </p>
          )}
        </section>
      )}

      {template.kommentare.opsKommentare.length > 0 && (
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Ops-Kommentare</h2>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {template.kommentare.opsKommentare.map((kommentar, index) => (
              <li key={index} className="py-2">
                <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{kommentar.body}</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {kommentar.authorName} · {new Date(kommentar.createdAtUtc).toLocaleString('de-DE')}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {template.positionen.length > 0 && (
        <CollapsibleSection title="Positionen">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {template.positionen.map((position) => (
              <li key={position.itemNumber} className="py-2">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {position.displayTitle}
                </p>
                {position.quantityLabel && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{position.quantityLabel}</p>
                )}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {hasTechnik && (
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Technik</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {template.technik.waermepumpenTyp && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Wärmepumpentyp</dt>
                <dd className="text-gray-900 dark:text-white">{template.technik.waermepumpenTyp}</dd>
              </div>
            )}
            {template.technik.kernbohrer && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Kernbohrer</dt>
                <dd className="text-gray-900 dark:text-white">{template.technik.kernbohrer}</dd>
              </div>
            )}
            {template.technik.standortAusseneinheitGeplant && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Standort Außeneinheit</dt>
                <dd className="text-gray-900 dark:text-white">
                  {template.technik.standortAusseneinheitGeplant}
                </dd>
              </div>
            )}
            {template.technik.standortInneneinheitGeplant && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Standort Inneneinheit</dt>
                <dd className="text-gray-900 dark:text-white">
                  {template.technik.standortInneneinheitGeplant}
                </dd>
              </div>
            )}
            {template.technik.inneneinheitRohranschluss && rohranschlussPairs.length === 0 && (
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Rohranschluss Inneneinheit</dt>
                <dd className="text-gray-900 dark:text-white">
                  {template.technik.inneneinheitRohranschluss}
                </dd>
              </div>
            )}
          </dl>
          {rohranschlussPairs.length > 0 && (
            <div className="mt-3">
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">Rohranschluss Inneneinheit</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {rohranschlussPairs.map((pair) => {
                  const displayFoto = pair.marked ?? pair.original;
                  return (
                    <button
                      key={pair.original.id}
                      type="button"
                      onClick={() => handleOpenRohranschlussFoto(pair)}
                      className="block w-full text-left"
                    >
                      <div className="relative aspect-square rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
                        {thumbnailCache[displayFoto.id] ? (
                          <img
                            src={thumbnailCache[displayFoto.id]}
                            alt={displayFoto.name}
                            className="w-full h-full object-cover"
                          />
                        ) : failedThumbnailIds[displayFoto.id] ? (
                          <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 p-2 text-center">
                            {displayFoto.name}
                          </div>
                        ) : (
                          <div
                            className="w-full h-full animate-pulse bg-gray-200 dark:bg-gray-600"
                            aria-label="Foto wird geladen"
                          />
                        )}
                        {displayFoto.description && (
                          <CaptionIndicator caption={displayFoto.description} />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {template.subunternehmer.length > 0 && (
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Beauftragte Subunternehmer
          </h2>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {template.subunternehmer.map((sub, index) => (
              <li key={`${sub.trade}-${index}`} className="py-2 flex justify-between items-start gap-2">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {sub.isHubSpotDirect
                      ? `Auftragnehmer: ${sub.contractor || '—'}`
                      : sub.displayName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{sub.trade}</p>
                  {/* Ticket #1055: aktueller Termin/Beauftragungsstand aus
                      SubcontractorProjectTradeSnapshots, zusätzlich zur reinen Beauftragungsinfo
                      oben. */}
                  {sub.contractor && !sub.isHubSpotDirect && sub.contractor !== sub.displayName && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Ausführung: {sub.contractor}</p>
                  )}
                  {sub.scheduledDate && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Termin: {sub.scheduledDate}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  {sub.email && (
                    <a href={`mailto:${sub.email}`} className="text-xs text-primary-600 hover:underline">
                      {sub.email}
                    </a>
                  )}
                  {sub.ordered !== null && sub.ordered !== undefined && (
                    <span
                      className={`text-xs font-medium rounded px-2 py-0.5 ${
                        sub.ordered
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                      }`}
                    >
                      {sub.ordered ? 'Bestellt' : 'Nicht bestellt'}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(briefingDokumente.length > 0 || template.isOpsManager) && (
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Briefing-Dokumente</h2>
          {briefingDokumente.length > 0 ? (
            <ul className="space-y-2 mb-4">
              {briefingDokumente.map((document) => (
                <li key={document.id}>
                  <a
                    href={document.webViewLink}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm text-primary-700 hover:border-primary-400 dark:border-gray-600 dark:text-primary-300"
                  >
                    <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="truncate">{document.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">Noch keine Briefing-Dokumente abgelegt.</p>
          )}

          {template.isOpsManager && !disabled && (
            <div className="space-y-2">
              {pendingBriefingFiles.length > 0 && (
                <ul className="space-y-2">
                  {pendingBriefingFiles.map((file) => (
                    <li key={file.id} className="flex items-center justify-between rounded border px-3 py-2 text-sm dark:border-gray-600 dark:text-white">
                      <span className="truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setPendingBriefingFiles((previous) => previous.filter((entry) => entry.id !== file.id))}
                        className="ml-3 text-red-600"
                      >
                        Entfernen
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                onClick={() => setShowBriefingUploadModal(true)}
                disabled={uploadingBriefingFiles}
                className="w-full rounded-lg border-2 border-dashed border-gray-300 px-4 py-3 text-sm dark:border-gray-600 dark:text-gray-200 disabled:opacity-50"
              >
                {pendingBriefingFiles.length ? 'Weitere Dateien hinzufügen' : 'Briefing-Dokumente auswählen'}
              </button>
              {pendingBriefingFiles.length > 0 && (
                <button
                  type="button"
                  onClick={() => void handleBriefingUpload()}
                  disabled={uploadingBriefingFiles}
                  className="w-full rounded-lg bg-primary-600 px-4 py-3 font-medium text-white disabled:opacity-50"
                >
                  {uploadingBriefingFiles ? 'Wird hochgeladen …' : `${pendingBriefingFiles.length} Datei(en) hochladen`}
                </button>
              )}
            </div>
          )}
        </section>
      )}

      <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Rohre und Fittinge</h2>
        {template.isOpsManager ? (
          <div className="space-y-2">
            <textarea
              value={rohreFittingeDraft}
              onChange={(event) => setRohreFittingeDraft(event.target.value)}
              rows={4}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
              placeholder="Bereits installierte und benötigte Rohre/Fittinge inkl. Durchmesser und Länge"
            />
            <button
              type="button"
              onClick={handleSaveRohreFittinge}
              disabled={savingRohreFittinge}
              className="rounded-md bg-primary-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
            >
              {savingRohreFittinge ? 'Wird gespeichert...' : 'Speichern'}
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
            {template.rohreFittinge || '—'}
          </p>
        )}
      </section>

      {template.fotos.length > 0 && (
        <CollapsibleSection title="Fotos">
          <div className="space-y-4">
            {groupPairsByOrdner(pairFotosWithMarkedVersions(template.fotos)).map((group) => (
              <div key={group.ordner}>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                  {group.ordner}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {group.pairs.map((pair) => {
                    const displayFoto = pair.marked ?? pair.original;
                    return (
                      <button
                        key={pair.original.id}
                        type="button"
                        onClick={() => handleOpenFoto(pair)}
                        className="block w-full text-left"
                      >
                        <div className="relative aspect-square rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
                          {thumbnailCache[displayFoto.id] ? (
                            <img
                              src={thumbnailCache[displayFoto.id]}
                              alt={displayFoto.name}
                              className="w-full h-full object-cover"
                            />
                          ) : displayFoto.thumbnailLink && !failedThumbnailIds[displayFoto.id] ? (
                            <div
                              className="w-full h-full animate-pulse bg-gray-200 dark:bg-gray-600"
                              aria-label="Thumbnail wird geladen"
                            />
                          ) : isPdfFoto(displayFoto) ? (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-gray-500 dark:text-gray-400 p-2 text-center">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-xs line-clamp-2">{displayFoto.name}</span>
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 p-2 text-center">
                              {displayFoto.name}
                            </div>
                          )}
                          {displayFoto.description && (
                            <CaptionIndicator caption={displayFoto.description} />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {editingPair && (
        // Auf mobilen Bildschirmen volle Fläche (auch über der unteren Phasen-Navigation, deren
        // z-40 unter diesem z-50 liegt) — der Zeichen-Canvas braucht dort jeden verfügbaren
        // Millimeter (Nutzer-Rückmeldung). Ab `sm:` (Desktop) wieder ein zentrierter, begrenzter
        // Dialog wie die anderen Modals (z. B. DocumentPreviewModal) — auf großen Bildschirmen
        // wirkt "volle Fläche" überdimensioniert (ebenfalls Nutzer-Rückmeldung).
        <div className="fixed inset-0 z-50 flex flex-col sm:items-center sm:justify-center sm:bg-black sm:bg-opacity-75 sm:p-4 sm:pb-20">
          <div className="flex flex-col w-full h-full bg-white dark:bg-gray-800 sm:w-full sm:h-auto sm:max-w-2xl sm:max-h-[calc(90vh-60px)] sm:rounded-lg sm:shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                Foto markieren: {(editingPair.marked ?? editingPair.original).name}
              </h2>
              <button
                onClick={handleCancelAnnotation}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors shrink-0 ml-2"
                aria-label="Schließen"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
              {editorLoading ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Foto wird geladen</p>
                </div>
              ) : editorError || !editorImageDataUrl ? (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
                  {editorError || 'Foto konnte nicht geladen werden'}
                </div>
              ) : (
                <PhotoAnnotationEditor
                  imageDataUrl={editorImageDataUrl}
                  originalImageDataUrl={editorOriginalImageDataUrl}
                  initialCaption={(editingPair.marked ?? editingPair.original).description}
                  onSave={handleSaveAnnotation}
                  onDelete={editingPair.marked ? handleDeleteMarkedAnnotation : undefined}
                  onCancel={handleCancelAnnotation}
                  saving={savingAnnotation}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {editingRohranschlussPair && (
        <div className="fixed inset-0 z-50 flex flex-col sm:items-center sm:justify-center sm:bg-black sm:bg-opacity-75 sm:p-4 sm:pb-20">
          <div className="flex flex-col w-full h-full bg-white dark:bg-gray-800 sm:w-full sm:h-auto sm:max-w-2xl sm:max-h-[calc(90vh-60px)] sm:rounded-lg sm:shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                Foto markieren: {(editingRohranschlussPair.marked ?? editingRohranschlussPair.original).name}
              </h2>
              <button
                onClick={handleCancelRohranschlussAnnotation}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors shrink-0 ml-2"
                aria-label="Schließen"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
              {rohranschlussEditorLoading ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Foto wird geladen</p>
                </div>
              ) : rohranschlussEditorError || !rohranschlussEditorImageDataUrl ? (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
                  {rohranschlussEditorError || 'Foto konnte nicht geladen werden'}
                </div>
              ) : (
                <PhotoAnnotationEditor
                  imageDataUrl={rohranschlussEditorImageDataUrl}
                  originalImageDataUrl={rohranschlussEditorOriginalImageDataUrl}
                  initialCaption={(editingRohranschlussPair.marked ?? editingRohranschlussPair.original).description}
                  onSave={handleSaveRohranschlussAnnotation}
                  onDelete={editingRohranschlussPair.marked ? handleDeleteMarkedRohranschlussAnnotation : undefined}
                  onCancel={handleCancelRohranschlussAnnotation}
                  saving={savingRohranschlussAnnotation}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {previewLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
        </div>
      )}

      {previewFoto && previewDataUrl && (
        <DocumentPreviewModal
          isOpen={true}
          onClose={handleClosePreview}
          documentName={previewFoto.name}
          documentType={isPdfFoto(previewFoto) ? 'pdf' : 'image'}
          previewData={previewDataUrl}
          caption={previewFoto.description}
        />
      )}

      <FileUploadModal
        isOpen={showBriefingUploadModal}
        onClose={() => setShowBriefingUploadModal(false)}
        onUpload={handleBriefingFilesSelected}
        title="Briefing-Dokumente hinzufügen"
        allowedTypes={['image/jpeg', 'image/png', 'application/pdf']}
        multiUpload={true}
      />
    </div>
  );
};

export default ProjektvorbereitungBriefing;
